import React, { useEffect, useState } from 'react'
import { useDispatch } from "react-redux";
import ReactSelect from "react-select"
import dayjs from "dayjs";
import { Box, FormControl, Menu, MenuItem, Stack, Typography } from "@mui/material";
// utils and helpers
import { formattedDate, sortsBy } from "utilities/common";
import { getAllBlazeCategory, getAllCategory } from "../redux/slices/hyperwolf/products";
import { openSnackbar } from "../redux/slices/common";
import { getAllBrands, getBoxes, getDistributionConfig } from "../redux/slices/hyperwolf/inventoryDistribution";
import { getRegions } from "../redux/slices/hyperwolf/settings";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
// custom components
import MDButton from "components/MDButton";
import DateRange from "components/dateRangePicker/DateRange";
import FilterAltIcon from '@mui/icons-material/FilterAlt';

export const SubCategoryFilter = ({ value, handleChange, placeholder, selectAllLabel }) => {
  const dispatch = useDispatch();
  const [categories, setCategories] = useState([]);

  const handleCategoriesList = async () => {
    try {
      const response = await dispatch(getAllBlazeCategory({ limit: 200 })).unwrap();
      if (response?.values) {
        setCategories(response?.values || [])
      }
    }
    catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  useEffect(() => {
    handleCategoriesList();
  }, []);

  return (
    <ReactSelect
      className='cus-react-select'
      placeholder={placeholder || "Box Type"}
      components={{
        IndicatorSeparator: null
      }}
      styles={{
        placeholder: (base) => ({
          ...base,
          color: 'rgba(46, 46, 58, 0.5)',
        }),
        menu: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
      name="categoryId"
      value={
        categories && categories.length && value
          ? (() => {
            const selected = categories.find(option => option.id === value);
            return selected ? { value: selected.id, label: selected.name } : null;
          })()
          : null
      }
      onChange={selectedOption => {
        handleChange({
          target: {
            name: "categoryId",
            value: selectedOption ? selectedOption.value : "",
          }
        });
      }}
      options={[
        { value: "", label: selectAllLabel || "All Boxes" },
        ...(categories?.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(cat => ({
          value: cat.id,
          label: cat.name
        })) || [])
      ]}
    />
  )
}

export const BrandFilter = ({ value, handleChange, placeholder, selectAllLabel }) => {
  const dispatch = useDispatch();
  const [brands, setBrands] = useState([]);

  const handleBrandsList = async () => {
    try {
      const response = await dispatch(getAllBrands()).unwrap();
      const data = response?.data || [];
      if (data) {
        setBrands(data || [])
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error;
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning" }));
    }
  };

  useEffect(() => {
    handleBrandsList();
  }, []);

  return (
    <ReactSelect
      className='cus-react-select'
      name="brandId"
      value={
        brands && brands.length && value
          ? (() => {
            const selected = brands.find(option => option.brandId === value);
            return selected ? { value: selected.brandId, label: selected.brandName } : null;
          })()
          : null
      }
      onChange={selectedOption => {
        handleChange({
          target: {
            name: "brandId",
            value: selectedOption ? selectedOption.value : "",
          }
        });
      }}
      placeholder={placeholder || "Brands"}
      options={[
        { value: "", label: selectAllLabel || "All" },
        ...(brands?.slice().sort((a, b) => (a.brandName || '').localeCompare(b.brandName || '')).map(brand => ({
          value: brand.brandId,
          label: brand.brandName
        })) || [])
      ]}
      styles={{
        control: (provided) => ({
          ...provided,
          minHeight: 40,
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
      components={{
        IndicatorSeparator: null
      }}
    />
  )
}

export const CategoryFilter = ({ value, handleChange, placeholder, selectAllLabel }) => {
  const dispatch = useDispatch();
  const [categories, setCategories] = useState([]);

  const handleCategoriesList = async () => {
    try {
      const response = await dispatch(getAllCategory({ limit: 200 })).unwrap();
      const data = response?.values || []
      if (data) {
        setCategories(data || [])
      }
    }
    catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    }
  }

  useEffect(() => {
    handleCategoriesList();
  }, []);

  return (
    <ReactSelect
      className='cus-react-select'
      name="mainCategoryId"
      value={
        categories && categories.length && value
          ? (() => {
            const selected = categories.find(cat => cat.id === value);
            return selected ? { value: value, label: selected.categoryName } : null;
          })()
          : null
      }
      onChange={selectedOption => {
        handleChange({
          target: {
            name: "mainCategoryId",
            value: selectedOption ? selectedOption.value : ""
          }
        });
      }}
      placeholder={placeholder || "Categories"}
      options={[
        { value: "", label: selectAllLabel || "All" },
        ...(categories
          ?.slice()
          ?.sort((a, b) => (a.categoryName || '').localeCompare(b.categoryName || ''))
          .map(cat => ({
            value: cat._id,
            label: cat.categoryName
          })) || [])
      ]}
      styles={{
        control: (provided) => ({
          ...provided,
          minHeight: 40,
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
      components={{
        IndicatorSeparator: null
      }}
      isClearable
    />
  )
}

export const BoxFilter = ({ value, handleChange, placeholder, selectAllLabel }) => {
  const dispatch = useDispatch();
  const [boxes, setBoxes] = useState([]);

  const handleBoxesList = async () => {
    try {
      const response = await dispatch(getBoxes({ limit: 200 })).unwrap();
      if (response?.data) {
        setBoxes(response?.data || [])
      }
    }
    catch (error) {
      showToastMsg(error, "error")
    }
  }

  useEffect(() => {
    handleBoxesList();
  }, []);

  return (
    <ReactSelect
      className='cus-react-select'
      name="boxId"
      value={
        boxes && boxes.length && value
          ? (() => {
            const selected = boxes.find(box => box._id === value);
            return selected ? { value: value, label: selected.name } : null;
          })()
          : null
      }
      onChange={selectedOption => {
        handleChange({
          target: {
            name: "boxId",
            value: selectedOption ? selectedOption.value : ""
          }
        });
      }}
      options={[
        { value: "", label: selectAllLabel || "All Boxes" },
        ...(boxes
          ?.slice()
          ?.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          .map(box => ({
            value: box._id,
            label: box.name
          })) || [])
      ]}
      placeholder={placeholder || "Box Type"}
      styles={{
        control: (provided) => ({
          ...provided,
          minHeight: 40,
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
      components={{
        IndicatorSeparator: null
      }}
    />
  )
}

export const RegionFilter = ({ value, handleChange, placeholder, selectAllLabel }) => {
  const dispatch = useDispatch();
  const [regionsList, setRegionsList] = useState([]);

  const handleRegionsList = async (filters = {}) => {
    try {
      const response = await dispatch(getRegions({ ...filters, limit: 100 })).unwrap()
      if (response?.data) {
        setRegionsList(response?.data || [])
      }
    } catch (error) {
      showToastMsg(error, "error")
    }
  };

  useEffect(() => {
    handleRegionsList();
  }, []);

  return (
    <ReactSelect
      className='cus-react-select'
      name="regionId"
      value={
        regionsList && regionsList.length && value
          ? (() => {
            const selected = regionsList.find(region => region.regionId === value);
            return selected ? { value: value, label: selected.region } : null;
          })()
          : null
      }
      onChange={selectedOption => {
        handleChange({
          target: {
            name: "regionId",
            value: selectedOption ? selectedOption.value : ""
          }
        });
      }}
      options={[
        { value: "", label: selectAllLabel || "All Boxes" },
        ...(regionsList
          ?.slice()
          ?.sort((a, b) => (a.region || '').localeCompare(b.region || ''))
          .map(box => ({
            value: box.regionId,
            label: box.region
          })) || [])
      ]}
      placeholder={placeholder || "Box Type"}
      styles={{
        control: (provided) => ({
          ...provided,
          minHeight: 40,
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
      components={{
        IndicatorSeparator: null
      }}
    />
  )
}

export const DiscrepancyTypeFilter = ({ value, handleChange, placeholder, selectAllLabel }) => {
  const dispatch = useDispatch();
  const [discrepancyTypes, setDiscrepancyTypes] = useState([]);

  const handleDiscrepancyTypesList = async () => {
    try {
      const response = await dispatch(getDistributionConfig({})).unwrap()
      if (response?.data) {
        setDiscrepancyTypes(response?.data?.discrepancyTypes || [])
      }
    } catch (error) {
      showToastMsg(error, "error")
    }
  };

  useEffect(() => {
    handleDiscrepancyTypesList();
  }, []);

  return (
    <ReactSelect
      className='cus-react-select'
      name="discrepancyType"
      value={
        discrepancyTypes && discrepancyTypes.length && value
          ? { value: value, label: value }
          : null
      }
      onChange={selectedOption => {
        handleChange({
          target: {
            name: "discrepancyType",
            value: selectedOption ? selectedOption.value : ""
          }
        });
      }}
      options={[
        { value: "", label: selectAllLabel || "All" },
        { value: "extra quantity", label: "Extra Quantity" },
        ...(discrepancyTypes
          ?.slice()
          ?.sort((a, b) => (a || '').localeCompare(b || ''))
          .map(discrepancyType => ({
            value: discrepancyType,
            label: discrepancyType
          })) || [])
      ]}
      placeholder={placeholder || "Box Type"}
      styles={{
        control: (provided) => ({
          ...provided,
          minHeight: 40,
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
      components={{
        IndicatorSeparator: null
      }}
    />
  )
}

export const StatusFilter = ({ value, handleChange, placeholder, options }) => {
  return (
    <ReactSelect
      className='cus-react-select'
      name="status"
      value={options.find(option => option.value === value) || null}
      onChange={selectedOption => {
        handleChange({
          target: {
            name: "status",
            value: selectedOption ? selectedOption.value : ""
          }
        });
      }}
      options={options || []}
      placeholder={placeholder || "Status"}
      styles={{
        control: (provided) => ({
          ...provided,
          minHeight: 40,
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
      components={{
        IndicatorSeparator: null
      }}
    />
  )
}

export const DistributionFilter = ({ value, handleChange, placeholder }) => {
  const options = [
    { value: "", label: "All" },
    { value: "Distribution", label: "Distribution" },
    { value: "Refill", label: "Refill" },
  ];

  return (
    <ReactSelect
      className='cus-react-select'
      name="distributionType"
      value={options.find(option => option.value === value) || null}
      onChange={selectedOption => {
        handleChange({
          target: {
            name: "distributionType",
            value: selectedOption ? selectedOption.value : ""
          }
        });
      }}
      options={options || []}
      placeholder={placeholder || "Distribution Type"}
      styles={{
        control: (provided) => ({
          ...provided,
          minHeight: 40,
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
      components={{
        IndicatorSeparator: null
      }}
    />
  )
}

export const RevenueFilter = ({ value, handleChange, placeholder }) => {
  const options = [
    { value: "", label: "All" },
    { value: "Distribution", label: "Distribution" },
    { value: "Refill", label: "Refill" },
  ];

  return (
    <ReactSelect
      className='cus-react-select'
      name="distributionType"
      value={options.find(option => option.value === value) || null}
      onChange={selectedOption => {
        handleChange({
          target: {
            name: "distributionType",
            value: selectedOption ? selectedOption.value : ""
          }
        });
      }}
      options={options || []}
      placeholder={placeholder || "Distribution Type"}
      styles={{
        control: (provided) => ({
          ...provided,
          minHeight: 40,
        }),
        menu: (provided) => ({
          ...provided,
          zIndex: 9999,
        }),
      }}
      components={{
        IndicatorSeparator: null
      }}
    />
  )
}

export const DateFilter = ({ handleChange, resetDateFilter = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isCustomDate, setIsCustomDate] = useState(false)
  const [orderDateFilter, setOrderDateFilter] = useState([null, null])
  const [sortOrder, setSortOrder] = useState('All Time');

  const handleSelectDate = (sort) => {
    setSortOrder(sort?.value);
    setOrderDateFilter([null, null]);
    setIsCustomDate(false)
    setIsMenuOpen(null)

    const today = new Date();

    switch (sort?.value) {
      case 'today':
        handleChange({ from: formattedDate(today), to: formattedDate(today) });
        break;
      case 'week': {
        const from = new Date(today);
        from.setDate(today.getDate() - 6);
        handleChange({ from: formattedDate(from), to: formattedDate(today) });
        break;
      }
      case 'month': {
        const from = new Date(today);
        from.setMonth(today.getMonth() - 1);
        from.setDate(from.getDate() + 1); // To include today as last day
        handleChange({ from: formattedDate(from), to: formattedDate(today) });
        break;
      }
      case 'threeMonths': {
        const from = new Date(today);
        from.setMonth(today.getMonth() - 3);
        from.setDate(from.getDate() + 1); // To include today as last day
        handleChange({ from: formattedDate(from), to: formattedDate(today) });
        break;
      }
      case 'All Time':
        handleChange({ from: "", to: "" });
        break;
      default:
        break;
    }
  }

  const setDateRange = (dates) => {
    if (dates?.every(date => date !== null)) {
      setOrderDateFilter(dates)
      setSortOrder('')
      setIsMenuOpen(false)
      handleChange({ from: formattedDate(dates[0]), to: formattedDate(dates[1]) })
      return;
    }
    if (dates && dates.length === 2) {
      setOrderDateFilter(dates)
    }
  }

  useEffect(() => {
    setIsMenuOpen(false)
    setIsCustomDate(false)
    setOrderDateFilter([null, null])
    setSortOrder('All Time');
  }, [resetDateFilter])

  return (
    <FormControl className="order-calendar-filter">
      {/* Date select button */}
      <MDButton
        variant="outlined"
        autoFocus={false}
        onClick={(e) => setIsMenuOpen(e.currentTarget)}
        sx={{
          cursor: "pointer",
          color: "transparent !important",
          textShadow: "0 0 0 #2E2E3A",
          display: 'flex',
          alignItems: 'center',
          padding: '6px 16px',
          minWidth: "auto"
        }}
      >
        {isCustomDate
          ? (orderDateFilter?.[0] && orderDateFilter?.[1]
            ? `${dayjs(orderDateFilter?.[0]).format('DD/MM/YYYY')} - ${dayjs(orderDateFilter?.[1]).format('DD/MM/YYYY')}`
            : 'Custom Date') // Show the date range or a message if not selected
          : orderDateFilter?.every(date => date !== null)
            ? `${dayjs(orderDateFilter?.[0]).format('DD/MM/YYYY')} - ${dayjs(orderDateFilter?.[1]).format('DD/MM/YYYY')}`
            : sortOrder
              ? sortsBy?.find((sort) => sort?.value === sortOrder)?.key
              : ''
        }
        <FilterAltIcon className="filter-alt-icon" fontSize="large" style={{ fill: "#2E2E3A", marginLeft: "15px" }} />
      </MDButton>

      {/* Date dropdown */}
      <Menu className="sort-by-filter" anchorEl={isMenuOpen} open={Boolean(isMenuOpen)} onClose={() => setIsMenuOpen(null)}>
        {
          sortsBy?.map((sort => {
            return <>
              <MenuItem
                value={sort?.value || ''}
                onClick={() => {
                  handleSelectDate(sort)
                }}
                key={sort?.key}
                variant='contained'
                className={sort.value === sortOrder ? 'tab-active' : ''}
                size='small'
              >
                {sort?.key}
              </MenuItem>
            </>
          }))
        }
        {isCustomDate && <MenuItem><Typography fontSize={14} fontWeight={500} color='blue'>Custom</Typography></MenuItem>}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation()
            setIsCustomDate(true)
          }}
          className="date-filter-container"
        >
          {
            isCustomDate ? <>
              <Stack direction='column' className='filter-inner-container'>
                <Box className="filter-date-range" style={{ minWidth: "280px" }}>
                  <FormControl fullWidth>
                    <DateRange
                      setDateRange={setDateRange}
                      showIcon name='range'
                      dateRange={orderDateFilter}
                      title='Select Date'
                    />
                  </FormControl>
                </Box>
              </Stack>
            </> : 'Custom'
          }
        </MenuItem>
      </Menu>
    </FormControl>)
}
